// Editor for a group's peer grants — "which other persistent agents may this
// one talk to, and how". One row per target group; capabilities are opt-in and
// scoped (event kinds × deliveries, fact lanes) so the default posture stays
// restrictive: no grants → the group's threads get no peer tools at all.
import type {
  PersistentAgentEventDelivery,
  PersistentAgentPeerEventKind,
  PersistentAgentPeerGrantInput,
} from '@cortex/types';

const eventKinds: PersistentAgentPeerEventKind[] = [
  'instruction',
  'notification',
  'completion',
  'wake',
];
const eventDeliveries: PersistentAgentEventDelivery[] = [
  'now',
  'interrupt',
  'next-turn',
  'when-idle',
  'inbox',
];

/** A grant row under edit — same shape the API takes, plus a lanes text field. */
export interface IPeerGrantDraft {
  targetAgentId: string;
  listThreads: boolean;
  /** Read-only: may read the target's message log (transcript) for analysis. */
  readLogs: boolean;
  /**
   * Read-only: may subscribe to the target's activity/status stream (the
   * cross-fleet dreamer shape). Standalone — deliberately does NOT imply
   * listThreads, so saving a readActivity-only grant never widens it.
   */
  readActivity: boolean;
  startThread: boolean;
  /** One-way: a stopped thread is terminal and never wakes again. */
  stopThread: boolean;
  sendEvent: boolean;
  kinds: PersistentAgentPeerEventKind[];
  deliveries: PersistentAgentEventDelivery[];
  broadcastEvent: boolean;
  broadcastKinds: PersistentAgentPeerEventKind[];
  broadcastDeliveries: PersistentAgentEventDelivery[];
  putFact: boolean;
  /** Comma-separated lane list ('*' = any). Kept as text while editing. */
  lanes: string;
  /** May spawn ephemeral DELEGATED worker threads in the target group. `{}` when on (host defaults). */
  delegateTask: boolean;
  /** Original maxChildren/maxDepth overrides, preserved verbatim while the toggle stays on. */
  delegateTaskCaps: { maxChildren?: number; maxDepth?: number } | undefined;
  /** May create long-lived continuous operation workers in the target group. */
  manageWorkers: boolean;
  maxWorkers: number;
}

export function emptyPeerGrantDraft(): IPeerGrantDraft {
  return {
    targetAgentId: '',
    listThreads: false,
    readLogs: false,
    readActivity: false,
    startThread: false,
    stopThread: false,
    sendEvent: false,
    kinds: [...eventKinds],
    deliveries: [...eventDeliveries],
    broadcastEvent: false,
    broadcastKinds: [...eventKinds],
    broadcastDeliveries: [...eventDeliveries],
    putFact: false,
    lanes: '*',
    delegateTask: false,
    delegateTaskCaps: undefined,
    manageWorkers: false,
    maxWorkers: 10,
  };
}

/** API grant → editable draft. */
export function toPeerGrantDraft(grant: PersistentAgentPeerGrantInput): IPeerGrantDraft {
  const createDelete = grant.capabilities.startThread || !!grant.capabilities.stopThread;
  const communicate =
    !!grant.capabilities.sendEvent ||
    !!grant.capabilities.putFact ||
    !!grant.capabilities.delegateTask ||
    !!grant.capabilities.manageWorkers;
  const broadcast = !!grant.capabilities.broadcastEvent;
  // Its own independent toggle — a grant saved before "Read logs" existed has
  // no `readLogs`, so it stays off (absent = forbidden).
  const readLogs = !!grant.capabilities.readLogs;
  // readActivity is deliberately NOT folded into the listThreads derivation:
  // a readActivity-only grant (the reflection-pilot dreamer) has listThreads
  // off, and deriving it on would silently widen the grant on the next save.
  const readActivity = !!grant.capabilities.readActivity;
  return {
    targetAgentId: grant.targetAgentId,
    listThreads: createDelete || communicate || broadcast || readLogs,
    readLogs,
    readActivity,
    startThread: createDelete,
    stopThread: createDelete,
    sendEvent: communicate,
    kinds: [...eventKinds],
    deliveries: [...eventDeliveries],
    broadcastEvent: broadcast,
    broadcastKinds: [...eventKinds],
    broadcastDeliveries: [...eventDeliveries],
    putFact: communicate,
    lanes: '*',
    delegateTask: communicate,
    delegateTaskCaps: grant.capabilities.delegateTask,
    manageWorkers: communicate,
    maxWorkers: grant.capabilities.manageWorkers?.maxWorkers ?? 10,
  };
}

/** Editable draft → API grant. Null when the row is incomplete/no-op. */
export function fromPeerGrantDraft(draft: IPeerGrantDraft): PersistentAgentPeerGrantInput | null {
  if (!draft.targetAgentId) return null;
  const lanes = draft.lanes
    .split(',')
    .map((lane) => lane.trim())
    .filter(Boolean);
  const sendEvent =
    draft.sendEvent && draft.kinds.length > 0 && draft.deliveries.length > 0
      ? { kinds: draft.kinds, deliveries: draft.deliveries }
      : undefined;
  const broadcastEvent =
    draft.broadcastEvent && draft.broadcastKinds.length > 0 && draft.broadcastDeliveries.length > 0
      ? { kinds: draft.broadcastKinds, deliveries: draft.broadcastDeliveries }
      : undefined;
  const putFact = draft.putFact && lanes.length > 0 ? { lanes } : undefined;
  const delegateTask = draft.delegateTask ? (draft.delegateTaskCaps ?? {}) : undefined;
  const manageWorkers = draft.manageWorkers ? { maxWorkers: draft.maxWorkers } : undefined;
  if (
    !draft.listThreads &&
    !draft.readLogs &&
    // Without this leg a readActivity-only grant round-trips to null and the
    // console DELETES the row on save instead of preserving it.
    !draft.readActivity &&
    !draft.startThread &&
    !draft.stopThread &&
    !sendEvent &&
    !broadcastEvent &&
    !putFact &&
    !delegateTask &&
    !manageWorkers
  ) {
    return null;
  }
  return {
    targetAgentId: draft.targetAgentId,
    capabilities: {
      listThreads: draft.listThreads,
      startThread: draft.startThread,
      // Omitted when off (like stopThread) so an untouched grant's payload is
      // byte-for-byte what it was before this capability existed.
      ...(draft.readLogs ? { readLogs: true } : {}),
      ...(draft.readActivity ? { readActivity: true } : {}),
      ...(draft.stopThread ? { stopThread: true } : {}),
      ...(sendEvent ? { sendEvent } : {}),
      ...(broadcastEvent ? { broadcastEvent } : {}),
      ...(putFact ? { putFact } : {}),
      ...(delegateTask ? { delegateTask } : {}),
      ...(manageWorkers ? { manageWorkers } : {}),
    },
  };
}

export function PeerGrantsEditor({
  value,
  onChange,
  groups,
}: {
  value: IPeerGrantDraft[];
  onChange: (next: IPeerGrantDraft[]) => void;
  /** Candidate target groups (may include the group itself for self-spawn). */
  groups: { id: string; name: string }[];
}) {
  const patch = (i: number, part: Partial<IPeerGrantDraft>) =>
    onChange(value.map((row, j) => (j === i ? { ...row, ...part } : row)));

  const setPreset = (
    i: number,
    preset: 'create-delete' | 'communicate' | 'broadcast' | 'read-logs' | 'read-activity',
    enabled: boolean,
  ) => {
    const row = value[i];
    if (!row) return;
    const part: Partial<IPeerGrantDraft> =
      preset === 'create-delete'
        ? { startThread: enabled, stopThread: enabled }
        : preset === 'read-logs'
          ? { readLogs: enabled }
          : preset === 'read-activity'
            ? { readActivity: enabled }
            : preset === 'communicate'
              ? {
                  sendEvent: enabled,
                  kinds: [...eventKinds],
                  deliveries: [...eventDeliveries],
                  putFact: enabled,
                  lanes: '*',
                  delegateTask: enabled,
                  delegateTaskCaps: enabled ? row.delegateTaskCaps : undefined,
                  manageWorkers: enabled,
                }
              : {
                  broadcastEvent: enabled,
                  broadcastKinds: [...eventKinds],
                  broadcastDeliveries: [...eventDeliveries],
                };
    const createDelete = preset === 'create-delete' ? enabled : row.startThread && row.stopThread;
    const communicate =
      preset === 'communicate'
        ? enabled
        : row.sendEvent && row.putFact && row.delegateTask && row.manageWorkers;
    const broadcast = preset === 'broadcast' ? enabled : row.broadcastEvent;
    const readLogs = preset === 'read-logs' ? enabled : row.readLogs;
    patch(i, { ...part, listThreads: createDelete || communicate || broadcast || readLogs });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {value.map((row, i) => (
        <div
          key={i}
          className="card"
          style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}
        >
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select
              className="select-native"
              value={row.targetAgentId}
              onChange={(e) => patch(i, { targetAgentId: e.target.value })}
              style={{ flex: 1 }}
            >
              <option value="">Select target agent…</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
            <button
              className="btn btn--ghost btn--sm"
              aria-label="remove peer grant"
              onClick={() => onChange(value.filter((_, j) => j !== i))}
            >
              ✕
            </button>
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <label className="t-sm" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={row.startThread && row.stopThread}
                onChange={(e) => setPreset(i, 'create-delete', e.target.checked)}
              />
              Create / delete threads
            </label>
            <label className="t-sm" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={row.sendEvent && row.putFact && row.delegateTask && row.manageWorkers}
                onChange={(e) => setPreset(i, 'communicate', e.target.checked)}
              />
              Communicate
            </label>
            <label className="t-sm" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={row.broadcastEvent}
                onChange={(e) => setPreset(i, 'broadcast', e.target.checked)}
              />
              Broadcast
            </label>
            <label className="t-sm" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={row.readLogs}
                onChange={(e) => setPreset(i, 'read-logs', e.target.checked)}
              />
              Read logs
            </label>
            <label className="t-sm" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={row.readActivity}
                onChange={(e) => setPreset(i, 'read-activity', e.target.checked)}
              />
              Read activity
            </label>
          </div>
          <p className="t-xs t-muted" style={{ margin: 0 }}>
            Every option except Read activity includes listing threads. Communicate includes all
            message kinds, deliveries, state-signal lanes, delegated workers, and persistent
            operation workers. Create / delete grants start and stop access. Read logs lets this
            agent read the target agent&apos;s message log — its transcript of what it said and
            which tools it called — for analysis; it is read-only and cannot wake or steer the
            target. Read activity subscribes to the target&apos;s status/interaction stream (the
            cross-fleet reflection shape) — read-only and standalone.
          </p>
          {row.manageWorkers && (
            <label className="field" style={{ maxWidth: 220 }}>
              <span className="field-label">Worker cap</span>
              <input
                className="input"
                type="number"
                min={1}
                max={50}
                value={row.maxWorkers}
                onChange={(e) =>
                  patch(i, {
                    maxWorkers: Math.min(50, Math.max(1, Number(e.target.value) || 1)),
                  })
                }
              />
              <span className="field-help">Default 10, hard maximum 50.</span>
            </label>
          )}
        </div>
      ))}
      <div>
        <button
          className="btn btn--ghost btn--sm"
          onClick={() => onChange([...value, emptyPeerGrantDraft()])}
        >
          + Add peer permission
        </button>
      </div>
    </div>
  );
}
